import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ListTodo, 
  Target, 
  Repeat, 
  Plus, 
  Check, 
  Trash2,
  Calendar,
  Loader2,
  CheckCircle2,
  Circle,
  Flame
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorities = ['low', 'medium', 'high'];
const priorityColors = {
  low: 'bg-green-500/20 text-green-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  high: 'bg-red-500/20 text-red-400'
};

export default function LifeManagerPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Task form
  const [taskTitle, setTaskTitle] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');

  // Goal form
  const [goalTitle, setGoalTitle] = useState('');
  const [goalCategory, setGoalCategory] = useState('personal');

  // Habit form
  const [habitName, setHabitName] = useState('');
  const [habitFrequency, setHabitFrequency] = useState('daily');

  const loadData = useCallback(async () => {
    try {
      if (activeTab === 'tasks') {
        const response = await axios.get(`${API_URL}/api/tasks`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setTasks(response.data);
      } else if (activeTab === 'goals') {
        const response = await axios.get(`${API_URL}/api/goals`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setGoals(response.data);
      } else if (activeTab === 'habits') {
        const response = await axios.get(`${API_URL}/api/habits`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setHabits(response.data);
      }
    } catch (error) {
      console.error('Failed to load data');
    }
  }, [activeTab, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Task handlers
  const addTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/tasks`,
        { title: taskTitle, priority: taskPriority },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setTaskTitle('');
      toast.success('Task added');
      loadData();
    } catch (error) {
      toast.error('Failed to add task');
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      await axios.patch(
        `${API_URL}/api/tasks/${taskId}`,
        { completed: !completed },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Task deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Goal handlers
  const addGoal = async (e) => {
    e.preventDefault();
    if (!goalTitle.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/goals`,
        { title: goalTitle, category: goalCategory },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setGoalTitle('');
      toast.success('Goal added');
      loadData();
    } catch (error) {
      toast.error('Failed to add goal');
    } finally {
      setLoading(false);
    }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      await axios.patch(
        `${API_URL}/api/goals/${goalId}`,
        { progress, completed: progress >= 100 },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      loadData();
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API_URL}/api/goals/${goalId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Goal deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  // Habit handlers
  const addHabit = async (e) => {
    e.preventDefault();
    if (!habitName.trim()) {
      toast.error('Please enter a habit name');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/habits`,
        { name: habitName, frequency: habitFrequency },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setHabitName('');
      toast.success('Habit added');
      loadData();
    } catch (error) {
      toast.error('Failed to add habit');
    } finally {
      setLoading(false);
    }
  };

  const checkHabit = async (habitId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/habits/${habitId}/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      if (response.data.message === 'Already checked today') {
        toast.info('Already completed today!');
      } else {
        toast.success(`Streak: ${response.data.streak} days!`);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to check habit');
    }
  };

  const deleteHabit = async (habitId) => {
    try {
      await axios.delete(`${API_URL}/api/habits/${habitId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Habit deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete habit');
    }
  };

  const tabs = [
    { id: 'tasks', label: 'Tasks', icon: ListTodo },
    { id: 'goals', label: 'Goals', icon: Target },
    { id: 'habits', label: 'Habits', icon: Repeat },
  ];

  return (
    <div className="max-w-4xl mx-auto" data-testid="life-manager-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">AIRA Life Manager</h1>
        <p className="text-gray-400">Manage your tasks, goals, and habits</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`${tab.id}-tab`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-6">
          {/* Add Task Form */}
          <GlassCard className="p-6">
            <form onSubmit={addTask} className="flex gap-4">
              <input
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="input-field flex-1"
                placeholder="Add a new task..."
                data-testid="task-input"
              />
              <select
                value={taskPriority}
                onChange={(e) => setTaskPriority(e.target.value)}
                className="input-field w-32"
                data-testid="task-priority-select"
              >
                {priorities.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <button type="submit" disabled={loading} className="btn-primary px-6" data-testid="add-task-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              </button>
            </form>
          </GlassCard>

          {/* Task List */}
          <div className="space-y-2">
            <AnimatePresence>
              {tasks.map((task) => (
                <motion.div
                  key={task.task_id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                >
                  <GlassCard className="p-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => toggleTask(task.task_id, task.completed)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          task.completed
                            ? 'bg-primary border-primary'
                            : 'border-gray-600 hover:border-primary'
                        }`}
                        data-testid={`toggle-task-${task.task_id}`}
                      >
                        {task.completed && <Check className="w-4 h-4" />}
                      </button>
                      <span className={`flex-1 ${task.completed ? 'line-through text-gray-500' : ''}`}>
                        {task.title}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs ${priorityColors[task.priority]}`}>
                        {task.priority}
                      </span>
                      <button
                        onClick={() => deleteTask(task.task_id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                        data-testid={`delete-task-${task.task_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Goals Tab */}
      {activeTab === 'goals' && (
        <div className="space-y-6">
          <GlassCard className="p-6">
            <form onSubmit={addGoal} className="flex gap-4">
              <input
                type="text"
                value={goalTitle}
                onChange={(e) => setGoalTitle(e.target.value)}
                className="input-field flex-1"
                placeholder="Add a new goal..."
                data-testid="goal-input"
              />
              <select
                value={goalCategory}
                onChange={(e) => setGoalCategory(e.target.value)}
                className="input-field w-32"
                data-testid="goal-category-select"
              >
                <option value="personal">Personal</option>
                <option value="career">Career</option>
                <option value="health">Health</option>
                <option value="finance">Finance</option>
              </select>
              <button type="submit" disabled={loading} className="btn-primary px-6" data-testid="add-goal-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              </button>
            </form>
          </GlassCard>

          <div className="space-y-4">
            {goals.map((goal) => (
              <GlassCard key={goal.goal_id} className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {goal.completed ? (
                      <CheckCircle2 className="w-6 h-6 text-green-400" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-500" />
                    )}
                    <span className={goal.completed ? 'line-through text-gray-500' : 'font-medium'}>
                      {goal.title}
                    </span>
                  </div>
                  <button
                    onClick={() => deleteGoal(goal.goal_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${goal.progress}%` }}
                      className="h-full bg-gradient-to-r from-primary to-accent"
                    />
                  </div>
                  <span className="text-sm text-gray-400">{goal.progress}%</span>
                  <div className="flex gap-1">
                    {[25, 50, 75, 100].map((p) => (
                      <button
                        key={p}
                        onClick={() => updateGoalProgress(goal.goal_id, p)}
                        className={`px-2 py-1 text-xs rounded ${
                          goal.progress >= p ? 'bg-primary/20 text-primary' : 'bg-white/5 text-gray-500'
                        }`}
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {/* Habits Tab */}
      {activeTab === 'habits' && (
        <div className="space-y-6">
          <GlassCard className="p-6">
            <form onSubmit={addHabit} className="flex gap-4">
              <input
                type="text"
                value={habitName}
                onChange={(e) => setHabitName(e.target.value)}
                className="input-field flex-1"
                placeholder="Add a new habit..."
                data-testid="habit-input"
              />
              <select
                value={habitFrequency}
                onChange={(e) => setHabitFrequency(e.target.value)}
                className="input-field w-32"
                data-testid="habit-frequency-select"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <button type="submit" disabled={loading} className="btn-primary px-6" data-testid="add-habit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              </button>
            </form>
          </GlassCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {habits.map((habit) => (
              <GlassCard key={habit.habit_id} className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold">{habit.name}</h4>
                    <p className="text-sm text-gray-400 capitalize">{habit.frequency}</p>
                  </div>
                  <button
                    onClick={() => deleteHabit(habit.habit_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Flame className={`w-5 h-5 ${habit.streak > 0 ? 'text-orange-400' : 'text-gray-600'}`} />
                    <span className={`text-lg font-bold ${habit.streak > 0 ? 'text-orange-400' : 'text-gray-600'}`}>
                      {habit.streak}
                    </span>
                    <span className="text-sm text-gray-400">day streak</span>
                  </div>
                  <button
                    onClick={() => checkHabit(habit.habit_id)}
                    className="btn-primary px-4 py-2 text-sm"
                    data-testid={`check-habit-${habit.habit_id}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
